(() => {
  if (customElements.get('auvric-fit-finder')) return;

  const SIZE_ORDER = ['XXS','XS','S','M','L','XL','XXL','2XL','XXXL','3XL'];
  const NORMALIZE = (value) => {
    const v = String(value || '').trim().toUpperCase().replace(/\s+/g, '');
    if (v === '2XL') return 'XXL';
    if (v === '3XL') return 'XXXL';
    return v;
  };

  class AuvricFitFinder extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';
      this.overlay = this.querySelector('[data-fit-overlay]');
      this.form = this.querySelector('[data-fit-form]');
      this.result = this.querySelector('[data-fit-result]');
      this.heightInput = this.querySelector('[data-height-input]');
      this.weightInput = this.querySelector('[data-weight-input]');
      this.error = this.querySelector('[data-fit-error]');
      this.availableSizes = (this.dataset.sizes || '').split('|').map(v => v.trim()).filter(Boolean);
      this.recommended = null;
      this.heightUnit = 'cm';
      this.weightUnit = 'kg';

      this.querySelector('[data-fit-open]')?.addEventListener('click', () => this.open());
      this.querySelector('[data-fit-close]')?.addEventListener('click', () => this.close());
      this.overlay?.addEventListener('click', (e) => { if (e.target === this.overlay) this.close(); });
      this.querySelector('[data-fit-calculate]')?.addEventListener('click', () => this.calculate());
      this.querySelector('[data-fit-restart]')?.addEventListener('click', () => this.restart());
      this.querySelector('[data-fit-select]')?.addEventListener('click', () => this.selectRecommendedSize());
      this.querySelectorAll('[data-height-unit]').forEach(btn => btn.addEventListener('click', () => this.setHeightUnit(btn.dataset.heightUnit)));
      this.querySelectorAll('[data-weight-unit]').forEach(btn => btn.addEventListener('click', () => this.setWeightUnit(btn.dataset.weightUnit)));
      document.addEventListener('keydown', this.onKeydown = (e) => { if (e.key === 'Escape' && !this.overlay?.hidden) this.close(); });
    }

    disconnectedCallback() { document.removeEventListener('keydown', this.onKeydown); }

    open() {
      this.overlay.hidden = false;
      document.documentElement.style.overflow = 'hidden';
      requestAnimationFrame(() => this.querySelector('[data-fit-close]')?.focus());
    }

    close() {
      this.overlay.hidden = true;
      document.documentElement.style.overflow = '';
    }

    setHeightUnit(unit) {
      this.heightUnit = unit;
      this.querySelectorAll('[data-height-unit]').forEach(btn => btn.classList.toggle('is-active', btn.dataset.heightUnit === unit));
      this.querySelector('[data-height-cm]').hidden = unit !== 'cm';
      this.querySelector('[data-height-ft]').hidden = unit !== 'ft';
    }

    setWeightUnit(unit) {
      if (unit === this.weightUnit) return;
      const value = Number(this.weightInput.value);
      if (Number.isFinite(value) && value > 0) this.weightInput.value = unit === 'lb' ? Math.round(value * 2.20462) : (value / 2.20462).toFixed(1);
      this.weightUnit = unit;
      this.querySelectorAll('[data-weight-unit]').forEach(btn => btn.classList.toggle('is-active', btn.dataset.weightUnit === unit));
      this.weightInput.min = unit === 'lb' ? '88' : '40';
      this.weightInput.max = unit === 'lb' ? '397' : '180';
      this.weightInput.placeholder = unit === 'lb' ? 'e.g. 168' : 'e.g. 76';
    }

    getHeightCm() {
      if (this.heightUnit === 'cm') return Number(this.heightInput.value);
      const feet = Number(this.querySelector('[data-height-feet]').value);
      const inches = Number(this.querySelector('[data-height-inches]').value || 0);
      if (!Number.isFinite(feet)) return NaN;
      return (feet * 12 + inches) * 2.54;
    }

    getWeightKg() {
      const weight = Number(this.weightInput.value);
      return this.weightUnit === 'lb' ? weight / 2.20462 : weight;
    }

    calculate() {
      const height = this.getHeightCm();
      const weight = this.getWeightKg();
      if (!Number.isFinite(height) || !Number.isFinite(weight) || height < 145 || height > 215 || weight < 40 || weight > 180) {
        this.error.hidden = false;
        return;
      }
      this.error.hidden = true;
      const build = this.querySelector('input[name^="fit-build-"]:checked')?.value || 'average';
      const fit = this.querySelector('input[name^="fit-style-"]:checked')?.value || 'regular';

      // General men's tee baseline. Height and build nudge the recommendation; desired fit is the final preference adjustment.
      const thresholds = [56, 65, 74, 84, 95, 108, 122]; // XS through XXXL boundaries
      let index = 0;
      while (index < thresholds.length && weight > thresholds[index]) index++;
      index += 1; // threshold index 0 maps to XS at SIZE_ORDER index 1

      if (height < 165) index -= 0.25;
      else if (height >= 185 && height < 195) index += 0.25;
      else if (height >= 195) index += 0.5;

      if (build === 'slim') index -= 0.2;
      if (build === 'broad') index += 0.4;
      if (fit === 'sculpted') index -= 0.4;
      if (fit === 'relaxed') index += 0.65;

      index = Math.max(0, Math.min(SIZE_ORDER.length - 1, Math.round(index)));
      const target = SIZE_ORDER[index];
      const recommendation = this.nearestAvailable(target);
      if (!recommendation) return;
      this.recommended = recommendation;

      this.querySelector('[data-fit-size]').textContent = recommendation.label;
      const fitName = fit === 'sculpted' ? 'Sculpted fit' : fit === 'relaxed' ? 'Relaxed fit' : 'Regular fit';
      this.querySelector('[data-fit-headline]').textContent = fitName;
      this.querySelector('[data-fit-copy]').textContent = `Based on ${Math.round(height)} cm, ${Math.round(weight)} kg, your ${build} build and the ${fitName.toLowerCase()} you selected.`;
      this.querySelector('[data-fit-select]').textContent = `SELECT SIZE ${recommendation.label}`;
      this.form.hidden = true;
      this.result.hidden = false;
      this.querySelector('[data-fit-select]')?.focus();
    }

    nearestAvailable(target) {
      const targetIndex = SIZE_ORDER.indexOf(NORMALIZE(target));
      const candidates = this.availableSizes.map(label => ({ label, index: SIZE_ORDER.indexOf(NORMALIZE(label)) })).filter(x => x.index >= 0);
      if (!candidates.length) return null;
      candidates.sort((a,b) => Math.abs(a.index-targetIndex)-Math.abs(b.index-targetIndex) || a.index-b.index);
      return candidates[0];
    }

    selectRecommendedSize() {
      if (!this.recommended) return;
      const picker = this.closest('.product-details')?.querySelector('variant-picker') || document.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`);
      if (!picker) { this.close(); return; }
      const desired = NORMALIZE(this.recommended.label);
      const optionName = String(this.dataset.sizeOption || '').toLowerCase();
      const radios = [...picker.querySelectorAll('input[type="radio"][data-option-name]')];
      const radio = radios.find(input => String(input.dataset.optionName || '').toLowerCase() === optionName && NORMALIZE(input.value) === desired && input.getAttribute('aria-disabled') !== 'true');
      if (radio) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
        radio.dispatchEvent(new Event('input', { bubbles: true }));
      } else {
        const selects = [...picker.querySelectorAll('select.variant-option__select')];
        const select = selects.find(s => [...s.options].some(o => NORMALIZE(o.value) === desired));
        if (select) {
          const option = [...select.options].find(o => NORMALIZE(o.value) === desired);
          select.value = option.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
      this.close();
      picker.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    restart() {
      this.result.hidden = true;
      this.form.hidden = false;
    }
  }

  customElements.define('auvric-fit-finder', AuvricFitFinder);
})();
